import {
  REVENUE_ACCOUNTS,
  OPEX_ACCOUNTS,
  CAPEX_ACCOUNTS,
  JOB_TYPE_KEYWORDS,
  DEFAULT_JOB_TYPE,
  EXPENSE_CATEGORIES,
  BUDGET_SEGMENTS,
  VAT_RATE,
  CUSTOMER_TYPE_MAP,
} from '../constants';
import { isServiceOrLabour } from '../../../../utils/validation';
import { toDate, isWithinRange } from './weekHelpers';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function matchesAccount(modeOfPayment, accountList) {
  if (!modeOfPayment) return false;
  const lower = modeOfPayment.toLowerCase();
  return accountList.some(kw => lower.includes(kw));
}

function classifyJobType(invoice, job) {
  const textParts = [];
  if (invoice?.cartItems) {
    invoice.cartItems.forEach(ci => textParts.push((ci.description || '').toLowerCase()));
  }
  if (job?.primaryConcern) textParts.push(job.primaryConcern.toLowerCase());
  if (job?.technicianObservation) textParts.push(job.technicianObservation.toLowerCase());
  if (job?.workRequired) textParts.push(job.workRequired.toLowerCase());

  const combined = textParts.join(' ');

  for (const [type, keywords] of Object.entries(JOB_TYPE_KEYWORDS)) {
    if (keywords.some(kw => combined.includes(kw))) return type;
  }
  return DEFAULT_JOB_TYPE;
}

function classifyBudgetSegment(description) {
  const d = (description || '').toLowerCase();
  if (d.includes('body') || d.includes('panel') || d.includes('dent') || d.includes('bumper')) return 'Body Work';
  if (d.includes('paint') || d.includes('spray')) return 'Paint';
  if (d.includes('electrical') || d.includes('wiring') || d.includes('battery') || d.includes('alternator')) return 'Electrical';
  if (d.includes('fleet')) return 'Fleet';
  if (d.includes('special') || d.includes('custom')) return 'Special';
  return 'Mechanical';
}

function resolveCustomerType(customerName, customersByName) {
  if (!customerName) return 'Cash';
  const cust = customersByName[customerName.toLowerCase()];
  if (!cust) return 'Cash';
  return CUSTOMER_TYPE_MAP[cust.customerType] || 'Cash';
}

// ---------------------------------------------------------------------------
// Sheet 1 – Report Tab aggregations
// ---------------------------------------------------------------------------

/**
 * Compute opening/closing balances grouped by account type.
 */
export function computeBalances(preWeekTransactions, weekTransactions) {
  const accounts = { Revenue: 0, Opex: 0, CAPEX: 0, Other: 0 };

  function classifyAccount(t) {
    const mop = t.modeOfPayment || '';
    if (matchesAccount(mop, REVENUE_ACCOUNTS)) return 'Revenue';
    if (matchesAccount(mop, OPEX_ACCOUNTS)) return 'Opex';
    if (matchesAccount(mop, CAPEX_ACCOUNTS)) return 'CAPEX';
    return 'Other';
  }

  function applyTxn(t, target) {
    const acct = classifyAccount(t);
    const amt = Number(t.amount) || 0;
    target[acct] += t.type === 'Income' ? amt : -amt;
  }

  // Opening = all txns before this week
  const opening = { Revenue: 0, Opex: 0, CAPEX: 0, Other: 0 };
  preWeekTransactions.forEach(t => applyTxn(t, opening));

  // Delta = this week's txns
  const delta = { Revenue: 0, Opex: 0, CAPEX: 0, Other: 0 };
  weekTransactions.forEach(t => applyTxn(t, delta));

  // Closing = opening + delta
  const closing = {};
  Object.keys(opening).forEach(k => {
    closing[k] = opening[k] + delta[k];
  });

  return { opening, delta, closing };
}

/**
 * Revenue summary: Income transactions on Revenue account within the week.
 */
export function computeRevenueSummary(weekTransactions) {
  return weekTransactions
    .filter(t => t.type === 'Income' && matchesAccount(t.modeOfPayment, REVENUE_ACCOUNTS))
    .map(t => ({
      description: t.description || t.category || '',
      amount: Number(t.amount) || 0,
      modeOfPayment: t.modeOfPayment || '',
      date: t.transactionDate || '',
      invoiceNo: t.invoiceNo || '',
    }));
}

/**
 * Capital summary: Income transactions on CAPEX account within the week.
 */
export function computeCapitalSummary(weekTransactions) {
  return weekTransactions
    .filter(t => t.type === 'Income' && matchesAccount(t.modeOfPayment, CAPEX_ACCOUNTS))
    .map(t => ({
      description: t.description || t.category || '',
      amount: Number(t.amount) || 0,
      modeOfPayment: t.modeOfPayment || '',
      date: t.transactionDate || '',
    }));
}

/**
 * Opex summary: Expenditure transactions within the week.
 */
export function computeOpexSummary(weekTransactions) {
  return weekTransactions
    .filter(t => t.type === 'Expenditure')
    .map(t => ({
      description: t.description || t.category || '',
      amount: Number(t.amount) || 0,
      category: t.category || 'Miscellaneous',
      modeOfPayment: t.modeOfPayment || '',
      date: t.transactionDate || '',
    }));
}

/**
 * Vehicle job details: invoice-level rows with parts/labour split.
 */
export function computeVehicleJobDetails(weekInvoices, jobsByNo, customersByName) {
  return weekInvoices.map(inv => {
    const job = inv.jobCardNo ? jobsByNo[inv.jobCardNo] : null;
    const jobType = classifyJobType(inv, job);
    const custType = resolveCustomerType(inv.customerName, customersByName);

    let spareParts = 0;
    let labour = 0;
    (inv.cartItems || []).forEach(ci => {
      const amt = (Number(ci.price) || 0) * (Number(ci.quantity) || 1);
      if (isServiceOrLabour(ci)) {
        labour += amt;
      } else {
        spareParts += amt;
      }
    });

    // VAT
    let vat = 0;
    if (inv.includeTax) {
      if (inv.taxOnServicesOnly) {
        vat = labour * VAT_RATE;
      } else {
        vat = (spareParts + labour) * VAT_RATE;
      }
    }

    return {
      invoiceNumber: inv.invoiceNumber || '',
      customerName: inv.customerName || '',
      registrationNumber: inv.registrationNumber || '',
      jobCardNo: inv.jobCardNo || '',
      jobType,
      customerType: custType,
      spareParts,
      labour,
      vat,
      total: spareParts + labour + vat,
    };
  });
}

/**
 * Weekly cash position: inflows vs outflows by category.
 */
export function computeCashPosition(weekTransactions) {
  let totalInflows = 0;
  let totalOutflows = 0;
  const outflowsByCategory = Object.create(null);

  weekTransactions.forEach(t => {
    const amt = Number(t.amount) || 0;
    if (t.type === 'Income') {
      totalInflows += amt;
    } else {
      totalOutflows += amt;
      const cat = normaliseCategoryToExpense(t.category || t.description || 'Miscellaneous');
      outflowsByCategory[cat] = (outflowsByCategory[cat] || 0) + amt;
    }
  });

  return { totalInflows, totalOutflows, outflowsByCategory, net: totalInflows - totalOutflows };
}

function normaliseCategoryToExpense(raw) {
  const lower = (raw || '').toLowerCase();
  for (const cat of EXPENSE_CATEGORIES) {
    if (lower.includes(cat.toLowerCase())) return cat;
  }
  if (lower.includes('spare') || lower.includes('part')) return 'Spare Parts';
  if (lower.includes('diesel') || lower.includes('fuel')) return 'Diesel';
  if (lower.includes('utility') || lower.includes('nepa') || lower.includes('power')) return 'Utilities';
  if (lower.includes('logist') || lower.includes('transport')) return 'Logistics';
  if (lower.includes('salary') || lower.includes('wage')) return 'Salaries';
  if (lower.includes('contract')) return 'Contractor';
  if (lower.includes('rent')) return 'Rent';
  if (lower.includes('internet') || lower.includes('wifi')) return 'Internet';
  return 'Miscellaneous';
}

// ---------------------------------------------------------------------------
// Sheet 2 – Budget Review Tab aggregations
// ---------------------------------------------------------------------------

/**
 * Budget review: month invoices split into segments × spare part / labour,
 * with weekly column based on which week of the month the invoice falls into.
 */
export function computeBudgetReview(monthInvoices, monthStart, jobsByNo, customersByName) {
  // Determine weeks within the month (Mon-Sun)
  const weeks = [];
  let wStart = new Date(monthStart);
  // Adjust to Monday if monthStart isn't Monday
  const dayOfWeek = wStart.getDay();
  if (dayOfWeek !== 1) {
    // Keep month start as-is for first partial week
  }

  // Build week boundaries
  const monthEndDate = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
  let cursor = new Date(monthStart);
  let weekIdx = 0;
  while (cursor <= monthEndDate) {
    const wEnd = new Date(cursor);
    // Find next Sunday or month end
    const daysToSunday = (7 - wEnd.getDay()) % 7;
    wEnd.setDate(wEnd.getDate() + daysToSunday);
    if (wEnd > monthEndDate) wEnd.setTime(monthEndDate.getTime());
    wEnd.setHours(23, 59, 59, 999);

    weeks.push({ start: new Date(cursor), end: wEnd, label: `Wk ${weekIdx + 1}` });
    cursor = new Date(wEnd);
    cursor.setDate(cursor.getDate() + 1);
    cursor.setHours(0, 0, 0, 0);
    weekIdx++;
  }

  // For each segment: { segment, spareParts: { wk1, wk2, ... }, labour: { wk1, wk2, ... } }
  const segmentData = {};
  BUDGET_SEGMENTS.forEach(seg => {
    segmentData[seg] = {
      spareParts: weeks.map(() => 0),
      labour: weeks.map(() => 0),
    };
  });

  monthInvoices.forEach(inv => {
    const invDate = toDate(inv.invoiceDate) || toDate(inv.datePosted);
    if (!invDate) return;

    // Find which week this invoice falls into
    const wIdx = weeks.findIndex(w => isWithinRange(invDate, w.start, w.end));
    if (wIdx === -1) return;

    (inv.cartItems || []).forEach(ci => {
      const amt = (Number(ci.price) || 0) * (Number(ci.quantity) || 1);
      const segment = classifyBudgetSegment(ci.description);
      if (!segmentData[segment]) return;

      if (isServiceOrLabour(ci)) {
        segmentData[segment].labour[wIdx] += amt;
      } else {
        segmentData[segment].spareParts[wIdx] += amt;
      }
    });
  });

  return { weeks, segmentData };
}

// ---------------------------------------------------------------------------
// Sheet 3 – Breakdown Tab aggregations
// ---------------------------------------------------------------------------

/**
 * Detailed expense breakdown: individual line items grouped by expense category.
 */
export function computeExpenseBreakdown(weekTransactions) {
  const grouped = Object.create(null);
  EXPENSE_CATEGORIES.forEach(cat => { grouped[cat] = []; });

  weekTransactions
    .filter(t => t.type === 'Expenditure')
    .forEach(t => {
      const cat = normaliseCategoryToExpense(t.category || t.description || 'Miscellaneous');
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push({
        description: t.description || '',
        amount: Number(t.amount) || 0,
        date: t.transactionDate || '',
        modeOfPayment: t.modeOfPayment || '',
        invoiceNo: t.invoiceNo || '',
      });
    });

  return grouped;
}

/**
 * Receivables aging: invoices with receivable > 0, grouped by customer × month.
 */
export function computeReceivablesAging(allInvoices) {
  const aging = Object.create(null);
  allInvoices
    .filter(inv => (Number(inv.receivable) || 0) > 0)
    .forEach(inv => {
      const name = String(inv.customerName || 'Unknown');
      const d = toDate(inv.invoiceDate) || toDate(inv.datePosted);
      const monthKey = d
        ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        : 'Unknown';

      if (!aging[name]) aging[name] = Object.create(null);
      aging[name][monthKey] = (aging[name][monthKey] || 0) + Number(inv.receivable);
    });
  return aging;
}

/**
 * Payables aging: open payables grouped by supplier × month.
 */
export function computePayablesAging(openPayables) {
  const aging = Object.create(null);
  openPayables.forEach(p => {
    const supplier = String(p.supplier || 'Unknown');
    const d = toDate(p.dateOfPurchase) || toDate(p.dateRequired) || toDate(p.statusUpdateDate);
    const monthKey = d
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      : 'Unknown';

    const outstanding = (Number(p.grandTotal) || 0) - (Number(p.totalAmountPaid) || 0);
    if (outstanding <= 0) return;

    if (!aging[supplier]) aging[supplier] = Object.create(null);
    aging[supplier][monthKey] = (aging[supplier][monthKey] || 0) + outstanding;
  });
  return aging;
}

// ---------------------------------------------------------------------------
// Sheet 4 – Summary Tab aggregations
// ---------------------------------------------------------------------------

/**
 * Monthly summary: total income, spare part margin, labour, gross profit.
 */
export function computeMonthlySummary(monthInvoices) {
  let totalIncome = 0;
  let totalSpareSale = 0;
  let totalSpareCost = 0;
  let totalLabour = 0;

  monthInvoices.forEach(inv => {
    const invTotal = Number(inv.totalSale) || Number(inv.grandTotal) || 0;
    totalIncome += invTotal;

    (inv.cartItems || []).forEach(ci => {
      const saleAmt = (Number(ci.price) || 0) * (Number(ci.quantity) || 1);
      if (isServiceOrLabour(ci)) {
        totalLabour += saleAmt;
      } else {
        totalSpareSale += saleAmt;
        totalSpareCost += (Number(ci.costPrice) || 0) * (Number(ci.quantity) || 1);
      }
    });
  });

  const sparePartMargin = totalSpareSale - totalSpareCost;
  const grossProfit = sparePartMargin + totalLabour;

  return {
    totalIncome,
    totalSpareSale,
    totalSpareCost,
    sparePartMargin,
    totalLabour,
    grossProfit,
    invoiceCount: monthInvoices.length,
  };
}
